import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "@/ch.css";
import { BRAND } from "@/brand";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitch, useLang } from "@/i18n";
import { api, COMPANY_KEY, errMsg, ROLE_LABEL } from "@/lib/api";
import { FL, initials } from "@/components/ch/ui";
import { Welcome } from "@/pages/Onboarding";

const NAV = [["/", "Dashboard", "◧"], ["/tasks", "Compliances", "✔"], ["/calendar", "Calendar", "▦"], ["/billing", "Billing", "◈"], ["/documents", "Documents", "❏"], ["/team", "Team", "☻"], ["/requests/new", "Request a Service", "💬"]];
const MENU = [["/requests", "Support", "?", "menu-support"], ["/company", "Company profile", "▣", "menu-company"], ["/account", "Account", "⚙", "menu-account"]];
const SHORT = { Dashboard: "Home", Compliances: "Compliance", Documents: "Docs", "Request a Service": "Request", Billing: "Payments" };
const TITLES = { "/tasks": "Compliances", "/calendar": "Calendar", "/billing": "Billing", "/documents": "Documents", "/team": "Team", "/requests/new": "Request a Service" };
const THEME_KEY = "ch_theme";
const tid = (l) => l.toLowerCase().replace(/ /g, "-");

export function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem(THEME_KEY) === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    return () => { document.documentElement.classList.remove("dark"); };
  }, [dark]);
  return [dark, () => setDark((v) => !v)];
}

function AccountMenu({ user, logout, allowed }) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  const list = (user.memberships || []).filter((m) => m.company_name);
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("click", out); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("click", out); document.removeEventListener("keydown", esc); };
  }, []);
  const pick = (id) => {
    setOpen(false);
    if (id === user.tenant_id) return;
    setSwitching(true);
    localStorage.setItem(COMPANY_KEY, id);
    window.location.assign(window.location.pathname);
  };
  const go = (to) => { setOpen(false); nav(to); };
  return (
    <div className="acw" ref={ref}>
      <button className="acct" aria-haspopup="true" aria-expanded={open} onClick={() => setOpen(!open)} data-testid={list.length > 1 ? "company-switcher" : "user-menu-btn"}>
        <span className="av" data-testid="header-user-initials">{switching ? "…" : initials(user.name)}</span>
        <span className="t"><b data-testid="header-user-name">{user.name}</b><small data-testid="header-company-name">{switching ? "Switching company…" : user.company_name || "Company unavailable"}</small></span><span className="cv">▾</span>
      </button>
      <div className={`menu ${open ? "on" : ""}`} data-testid="account-menu">
        <div className="mu0 r"><span className="av lg">{initials(user.name)}</span><div style={{ minWidth: 0 }}><b>{user.name}</b><small>{user.email}</small></div></div>
        <div className="ml">Switch company</div>
        {list.map((m) => (
          <button key={m.tenant_id} className={`mi ${m.tenant_id === user.tenant_id ? "on" : ""}`} onClick={() => pick(m.tenant_id)} data-testid={`company-option-${m.tenant_id}`}>
            <span className="ci">{initials(m.company_name)}</span><div className="gr"><b>{m.company_name}</b><small>{ROLE_LABEL[m.role]}</small></div>{m.tenant_id === user.tenant_id && <span className="ck0">✓</span>}
          </button>
        ))}
        {user.perms?.requests && <button className="mi" onClick={() => go("/requests/new")} data-testid="menu-link-company"><span className="ci">+</span><div className="gr"><b>Link another company</b><small>Send a request to V.A</small></div></button>}
        {MENU.filter(allowed).map(([to, l, g, id]) => <button key={to} className="mi" onClick={() => go(to)} data-testid={id}><span className="ci">{g}</span><div className="gr"><b>{l}</b></div></button>)}
        <button className="mi sg" onClick={logout} data-testid="logout-btn"><span className="ci">⎋</span><div className="gr"><b>Sign out</b></div></button>
      </div>
    </div>
  );
}

function TrialBanner() {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => { if (user.perms?.billing_view) { api.get("/billing/account").then((r) => setC(r.data.commercial)).catch(() => {}); } }, [user.tenant_id, user.perms?.billing_view]);
  if (!c || !["trial", "past_due", "grace_period", "suspended", "expired"].includes(c.status)) return null;
  const cta = <Link className="b sm" to="/billing" data-testid="commercial-banner-link">{c.status === "trial" || c.status === "expired" ? "Choose a plan" : "View billing"}</Link>;
  if (c.status === "trial") {
    const t = c.trial || {};
    const total = t.start_date && t.end_date ? Math.round((new Date(t.end_date) - new Date(t.start_date)) / 86400000) + 1 : 14;
    return (
      <div className="tr" data-testid="commercial-banner"><div><b>Free trial · {c.days_left} day{c.days_left > 1 ? "s" : ""} left</b><small>Ends {FL(t.end_date)}</small><div className="tbar"><i style={{ width: `${Math.max(0, Math.min(100, ((total - c.days_left) / total) * 100))}%` }} /></div></div>{cta}</div>
    );
  }
  const msg = { past_due: ["An invoice is past due", "Please settle it to keep your account in good standing."], grace_period: ["Your account is in its grace period", "An invoice is overdue."],
    suspended: ["Account overdue", c.restrictions_on ? "New paid work is paused. Your records, downloads and billing stay available." : "Please settle outstanding invoices."], expired: ["Your trial has ended", "Your records are safe. Choose a plan to continue."] }[c.status];
  return <div className="tr ex" data-testid="commercial-banner"><div><b>{msg[0]}</b><small>{msg[1]}</small></div>{cta}</div>;
}

export default function ClientShell({ bell, allowed }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const home = pathname === "/";
  const [, toggleTheme] = useTheme();
  const { t } = useLang();
  const [dash, setDash] = useState(null);
  const [dashError, setDashError] = useState(null);
  const reload = useCallback(() => api.get("/dashboard").then((r) => { setDash(r.data); setDashError(null); }).catch((e) => setDashError(errMsg(e))), []);
  useEffect(() => { reload(); }, [reload, home]);
  const need = dash?.health?.need_you;
  const h = new Date().getHours();
  const title = home ? `${h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"}.` : TITLES[pathname];
  return (
    <div className="ch" data-testid="client-app">
      <div className="app">
        <nav className="sb" data-testid="client-sidebar">
          <div className="lg0" data-testid="sidebar-brand" translate="no"><img src={BRAND.logo} alt="FILIT" className="lgi" data-testid="sidebar-logo" />FILIT Compliance Hub</div>
          {NAV.filter(allowed).map(([to, label, g]) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `${isActive ? "on" : ""}${to === "/requests/new" ? " rq" : ""}`} data-testid={`nav-${tid(label)}`}>
              <span>{g}</span><em>{t(label)}</em><i className="sl">{t(SHORT[label] || label)}</i>{to === "/tasks" && need > 0 && <b data-testid="nav-compliances-badge">{need}</b>}
            </NavLink>
          ))}
        </nav>
        <main className="main">
          <TrialBanner />
          <div className="hd" data-testid="client-header">
            <div data-testid={home ? "dashboard-greeting" : "page-title"}>{title && <h1>{title}</h1>}
              {home && <p className="mu" data-testid="greeting-subtitle">{need == null ? "Loading your compliance summary…" : need ? `${need} item${need > 1 ? "s need" : " needs"} your attention today.` : "You’re all clear. Nothing needs your attention today."}</p>}</div>
            <div className="r tools"><LanguageSwitch />{bell}<button className="ib" onClick={toggleTheme} aria-label="Toggle theme" data-testid="theme-toggle-btn">◐</button><AccountMenu user={user} logout={logout} allowed={allowed} /></div>
          </div>
          <Outlet context={{ dash, dashError, reload }} />
          <footer className="foot" translate="no" data-testid="client-footer"><p className="mu">FILIT Compliance Hub</p><p className="cpy" data-testid="client-copyright">{BRAND.copyright}</p></footer>
        </main>
      </div>
      {user.role === "client_owner" && <Welcome />}
    </div>
  );
}
